import React from 'react';
import { Activity } from '@/services/dashboardService';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  CalendarClock,
  Package,
  Edit3,
  Plus,
  Trash2,
  DollarSign,
  Settings,
  User,
  Archive,
} from 'lucide-react';

import {
  useDashboardTranslations,
  type DashboardTranslations,
} from '@/hooks/useDashboardTranslations';

interface RecentActivityCardProps {
  activities: Activity[] | null;
  loading: boolean;
  maxItems?: number;
}

// Enhanced product name extraction with better patterns and translation support
const getProductName = (activity: Activity, i18n: DashboardTranslations): string => {
  const patterns = [
    /Created product '([^']+)'/,
    /Updated product '([^']+)'/,
    /Deleted product '([^']+)'/,
    /Archived product '([^']+)'/,
    /Updated pricing for '([^']+)'/,
    /Added price to '([^']+)'/,
    /Updated attributes for '([^']+)'/,
    /'([^']+)'\s*\(/, // Match 'ProductName' (SKU: ...)
    /product '([^']+)'/i,
    /product "([^"]+)"/i,
  ];

  for (const pattern of patterns) {
    const match = activity.message.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // Fallback: try to extract from message
  const fallbackMatch = activity.message.match(/([A-Za-z0-9\s\-_]+)/);
  if (fallbackMatch && fallbackMatch[1] && fallbackMatch[1].length > 3) {
    return fallbackMatch[1].trim();
  }

  return i18n.getRecentActivityText('productFallback');
};

// Translate activity message components
const getTranslatedMessage = (activity: Activity, i18n: DashboardTranslations): string => {
  const productName = getProductName(activity, i18n);

  // Try to match common patterns and replace with translated versions
  const message = activity.message.toLowerCase();

  if (message.includes('created product')) {
    return i18n.getActivityMessageText('createdProduct', { name: productName });
  }
  if (
    message.includes('updated product') &&
    !message.includes('pricing') &&
    !message.includes('attributes')
  ) {
    return i18n.getActivityMessageText('updatedProduct', { name: productName });
  }
  if (message.includes('deleted product')) {
    return i18n.getActivityMessageText('deletedProduct', { name: productName });
  }
  if (message.includes('archived product')) {
    return i18n.getActivityMessageText('archivedProduct', { name: productName });
  }
  if (message.includes('updated pricing') || message.includes('price')) {
    return i18n.getActivityMessageText('updatedPricing', { name: productName });
  }
  if (message.includes('added price')) {
    return i18n.getActivityMessageText('addedPrice', { name: productName });
  }
  if (message.includes('updated attributes') || message.includes('attribute')) {
    return i18n.getActivityMessageText('updatedAttributes', { name: productName });
  }

  // Fallback to original message if no pattern matches
  return activity.message;
};

// Extract SKU from message
const getProductSKU = (activity: Activity): string | null => {
  const skuMatch = activity.message.match(/\(SKU:\s*([^)]+)\)/);
  return skuMatch ? skuMatch[1].trim() : null;
};

// Extract family information
const getFamilyInfo = (activity: Activity): string | null => {
  const familyMatch = activity.message.match(/in\s+([^f]+)\s+family/);
  return familyMatch ? familyMatch[1].trim() : null;
};

// Extract and translate modification details
const getModificationDetails = (activity: Activity, i18n: DashboardTranslations): string | null => {
  const modMatch = activity.message.match(/modified\s+(.+)$/);
  if (!modMatch) return null;

  const fieldName = modMatch[1].trim().toLowerCase();

  // Try to translate common field names
  const fieldTranslations: Record<string, string> = {
    barcode: i18n.getFieldLabelText('barcode'),
    tags: i18n.getFieldLabelText('tags'),
    category: i18n.getFieldLabelText('category'),
    brand: i18n.getFieldLabelText('brand'),
    price: i18n.getFieldLabelText('price'),
    name: i18n.getFieldLabelText('name'),
    description: i18n.getFieldLabelText('description'),
    family: i18n.getFieldLabelText('family'),
    status: i18n.getFieldLabelText('status'),
  };

  const translatedField = fieldTranslations[fieldName] || fieldName;
  return i18n.getActivityMessageText('modified', { field: translatedField });
};

// Get action icon
const getActionIcon = (action: string) => {
  switch (action.toLowerCase()) {
    case 'created':
      return <Plus className="size-4 text-green-600 dark:text-green-400" />;
    case 'updated':
      return <Edit3 className="size-4 text-blue-600 dark:text-blue-400" />;
    case 'deleted':
      return <Trash2 className="size-4 text-red-600 dark:text-red-400" />;
    case 'archived':
      return <Archive className="size-4 text-orange-600 dark:text-orange-400" />;
    case 'bulk':
      return <Settings className="size-4 text-indigo-600 dark:text-indigo-400" />;
    case 'price':
      return <DollarSign className="size-4 text-yellow-600 dark:text-yellow-400" />;
    case 'attribute':
      return <Settings className="size-4 text-purple-600 dark:text-purple-400" />;
    default:
      return <Package className="size-4 text-gray-600 dark:text-gray-400" />;
  }
};

// Get action color
const getActionColor = (action: string) => {
  switch (action.toLowerCase()) {
    case 'created':
      return 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
    case 'updated':
      return 'bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
    case 'deleted':
      return 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
    case 'archived':
      return 'bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800';
    case 'bulk':
      return 'bg-indigo-50 dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800';
    case 'price':
      return 'bg-yellow-50 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
    case 'attribute':
      return 'bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800';
    default:
      return 'bg-gray-50 dark:bg-muted text-gray-700 dark:text-gray-400 border-gray-200 dark:border-border';
  }
};

export function RecentActivityCard({
  activities,
  loading,
  maxItems = 10,
}: RecentActivityCardProps) {
  const isValidActivities = Array.isArray(activities);
  const i18n = useDashboardTranslations();

  return (
    <Card className="h-full border-enterprise-200 bg-white shadow-sm dark:border-border dark:bg-card">
      <CardHeader className="border-b border-enterprise-100 pb-4 dark:border-border">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-enterprise-700 dark:text-foreground">
          <CalendarClock className="size-4" />
          {i18n.getRecentActivityText('title')}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-4 p-6">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex flex-1 items-center gap-4">
                  <Skeleton className="size-10 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="size-3" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                </div>
                <Skeleton className="h-4 w-12 shrink-0" />
              </div>
            ))}
          </div>
        ) : isValidActivities && activities.length > 0 ? (
          <div className="divide-y divide-enterprise-50 dark:divide-border">
            {activities.slice(0, maxItems).map((item, index) => {
              const productName = getProductName(item, i18n);
              const productSKU = getProductSKU(item);
              const familyInfo = getFamilyInfo(item);
              const modificationDetails = getModificationDetails(item, i18n);
              const translatedMessage = getTranslatedMessage(item, i18n);

              return (
                <div
                  key={item?.id || index}
                  className="hover:bg-enterprise-25 flex items-center justify-between px-6 py-4 transition-colors duration-150 dark:hover:bg-muted"
                >
                  {/* Left side: Avatar and main content */}
                  <div className="flex min-w-0 flex-1 items-center gap-4">
                    {/* User Avatar */}
                    <Avatar className="size-10 shrink-0">
                      <AvatarFallback className="bg-enterprise-100 text-xs font-medium text-enterprise-700 dark:bg-muted dark:text-foreground">
                        {item.user_name === i18n.getRecentActivityText('systemUser') ? (
                          <Settings className="size-4" />
                        ) : (
                          i18n.getUserInitials(
                            item.user_name || i18n.getRecentActivityText('userInitials.fallback')
                          )
                        )}
                      </AvatarFallback>
                    </Avatar>

                    {/* Main content area */}
                    <div className="min-w-0 flex-1">
                      {/* Top row: Action badge and product info */}
                      <div className="mb-2 flex items-center gap-3">
                        <Badge
                          variant="outline"
                          className={`flex items-center gap-1 text-xs font-medium ${getActionColor(item.action)}`}
                        >
                          {getActionIcon(item.action)}
                          <span>{i18n.getActivityActionText(item.action)}</span>
                        </Badge>

                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <Package className="size-4 shrink-0 text-enterprise-400 dark:text-muted-foreground" />
                          <span className="truncate font-medium text-enterprise-800 dark:text-foreground">
                            {productName}
                          </span>
                          {productSKU && (
                            <Badge
                              variant="outline"
                              className="shrink-0 text-xs text-enterprise-600 dark:border-border dark:text-muted-foreground"
                            >
                              {productSKU}
                            </Badge>
                          )}
                          {familyInfo && (
                            <Badge variant="secondary" className="shrink-0 text-xs">
                              {familyInfo}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Bottom row: User and modification details */}
                      <div className="flex items-center gap-2 text-sm text-enterprise-600 dark:text-muted-foreground">
                        <User className="size-3.5 shrink-0" />
                        <span className="font-medium">
                          {item.user_name || i18n.getRecentActivityText('unknownUser')}
                        </span>
                        {modificationDetails && (
                          <>
                            <span className="text-enterprise-400 dark:text-muted-foreground">
                              •
                            </span>
                            <span className="truncate text-enterprise-500 dark:text-muted-foreground">
                              {modificationDetails}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right side: Timestamp */}
                  <div className="ml-4 flex shrink-0 items-center text-xs text-enterprise-500 dark:text-muted-foreground">
                    <CalendarClock className="mr-1.5 size-3.5" />
                    <span className="font-medium">
                      {item?.created_at
                        ? i18n.getTimeAgoFormatted(item.created_at)
                        : i18n.getRecentActivityText('unknownTime')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-6 py-16">
            <div className="mb-4 rounded-full bg-enterprise-50 p-4 dark:bg-muted">
              <CalendarClock className="size-8 text-enterprise-400 dark:text-muted-foreground" />
            </div>
            <h3 className="mb-2 font-semibold text-enterprise-700 dark:text-foreground">
              {i18n.getRecentActivityText('noActivityTitle')}
            </h3>
            <p className="max-w-sm text-center text-sm text-enterprise-500 dark:text-muted-foreground">
              {i18n.getRecentActivityText('noActivityDesc')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
